import React,  { useState } from "react"

//model
import { DetailsModel } from "./Details.model";
import { IndustryType } from "../../interfaces/Industry";

//components
import { BasicButton, IconLibrary, ThriveIcon } from "thrive-components";
import TermsAndConditions from "../../components/FormInputs/TermsAndConditions/TermsAndConditions";
import SelectIndustry from "../../components/FormInputs/SelectIndustry/SelectIndustry";
import ValidationModal from "../../components/modals/Validation/ValidationModal";
import Password from "../../components/FormInputs/Password/password";
import FormTextInput from "../../components/FormInputs/TextInput/FormTextInput";

//hooks
import { useBeforeNavigateAway } from "thrive-components/hooks/useBeforeNavigateAway";
import useNavigatorSetup from "../../utils/hooks/useNavigatorSetup";
import { useObservable } from "thrive-observable-react";
import useScrollToTop from "../../utils/hooks/useScrollToTop";

//styles
import './details.scss';


const Details = () => {
    useNavigatorSetup();
    const [DM] = useState(() => new DetailsModel());

    //inputs
    const emailAddress = useObservable<string>(DM.emailAddress);
    const password = useObservable<string>(DM.password);
    const countdown = useObservable<number>(DM.countdown)
    const industry = useObservable<IndustryType | undefined>(DM.industry);
    const termsAndConditionsAgreed = useObservable<boolean>(DM.termsAndConditionsAgreed);
    const validationCode = useObservable<string>(DM.validationCode);    
    
    //validation
    const validateCases = useObservable<boolean>(DM.validateCases);
    const validateNumber = useObservable<boolean>(DM.validateNumber);
    const validateSpecialChar = useObservable<boolean>(DM.validateSpecialChar);
    const validateLength = useObservable<boolean>(DM.validateLength);
    const buttonIsDisabled = useObservable<boolean>(DM.buttonIsDisabled);
    const errorList = useObservable<string[]>(DM.errorList);
    const emailError = useObservable<string>(DM.emailError);
    const formHasBeenChanged = useObservable<boolean>(DM.formHasBeenChanged);
    const codeIsInvalid = useObservable<boolean>(DM.codeIsInvalid);

    //modals
    const showTermsAndConditionsModal = useObservable<boolean>(DM.showTermsAndConditionsModal);
    const showValidationModal = useObservable<boolean>(DM.showValidationModal);

    useBeforeNavigateAway(formHasBeenChanged);
    useScrollToTop();

    return (
        <div className="details-page page-container">
            <div className="details-container">
                <h1 className="details-heading">
                    {DM.title}
                </h1>

                <h2 className="details-sub-heading">
                    {DM.subtitle}
                </h2>

               <form className="reg-form">
                    <h3 className="details-form-heading">
                        {DM.userFormHeading}
                    </h3>
                    
                    <div className="name-input-wrapper">
                        <FormTextInput 
                            id={"first-name"} 
                            label={"First Name"}
                            value={DM.firstName.value}  
                            setValue={DM.setFirstname}  
                            errorMessage={"First name is required"} 
                            onBlur={() => DM.validateField("firstName")} 
                            showError={!!errorList.includes("firstName")}
                        />

                        <FormTextInput 
                            id={"last-name"} 
                            label={"Last Name"} 
                            value={DM.lastName.value} 
                            setValue={DM.setLastname} 
                            errorMessage={"Last name is required"} 
                            onBlur={() => DM.validateField("lastName")} 
                            showError={!!errorList.includes("lastName")}
                        />
                    </div>
            
                    <FormTextInput 
                        id={"email-address"} 
                        label={"Email Address"} 
                        type={"email"}
                        value={emailAddress} 
                        setValue={DM.setEmailAddress} 
                        errorMessage={emailError} 
                        onBlur={() => DM.validateField("emailAddressValidity")} 
                        showError={!!errorList.includes("emailAddress")}
                    />

                    <Password 
                        showError={!!errorList.includes("password")} 
                        setPassword={DM.setPassword} 
                        password={password} 
                        validateField={() => DM.validateField("password")} 
                        validateCases={validateCases} 
                        validateNumber={validateNumber} 
                        validateSpecialChar={validateSpecialChar} 
                        validateLength={validateLength}
                    />
                    
                    <h3 className="details-form-heading">
                        {DM.workspaceFormHeading}
                    </h3>             

                    <FormTextInput 
                        id={"workspace-name"} 
                        label={"Workspace Name"} 
                        value={DM.workspaceName.value}
                        placeholder={"e.g yourcompanyname"}
                        setValue={DM.setWorkspaceName} 
                        errorMessage={"Workspace name is required"} 
                        onBlur={() => DM.validateField("workspaceName")}
                        showError={!!errorList.includes("workspaceName")}
                    />

                    <SelectIndustry 
                        industry={industry} 
                        setIndustry={(value: IndustryType)=>DM.setIndustry(value)} 
                        showError={!!errorList.includes("industry")}
                        validateIndustry={() => DM.validateField("industry")}
                    />
                    
                    <TermsAndConditions 
                        termsAndConditionsAgreed={termsAndConditionsAgreed} 
                        setTermsAndConditionsAgreed={DM.setTermsAndConditionsAgreed} 
                        setShowTermsAndConditionsModal={()=>DM.setShowTermsAndConditionsModal(!showTermsAndConditionsModal)}
                        showTermsAndConditionsModal={showTermsAndConditionsModal}
                        setHideTermsAndConditionsModal={()=>DM.setShowTermsAndConditionsModal(false)}
                    />
                </form>

                <BasicButton 
                    className={`details-button`} 
                    content={
                        <>
                        {DM.button}
                        <ThriveIcon 
                            iconName={IconLibrary.ARROW_LEFT}
                            className="right-arrow"
                            color="var(--thriveWhite)"
                            sizeInPx="14"
                        />
                        </>
                    } 
                    ariaLabel={DM.button}     
                    onButtonClick={DM.onButtonClick}
                    isDisabled={buttonIsDisabled}
                />
            </div>

            <ValidationModal 
                showModal={showValidationModal} 
                onHideModal={() => DM.onHideValidationModal()} 
                emailAddress={emailAddress}
                code={validationCode} 
                countDown={countdown}
                setCode={DM.setValidationCode} 
                onResendCodeClick={()=>DM.onResendCodeClick()}
                onValidateClick={() => DM.onValidateClick()}     
                invalidCode={codeIsInvalid}      
            />
        </div>
    )
}

export default Details